import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ColourCombinations from '../ColourCombinations';
import { useColours, type ColourCombination } from '../../hooks/useColours';
import { useColourCombinationCatalog } from '../../hooks/useColourCombinationCatalog';
import { useProductRecords } from '../../hooks/useProductRecords';
import { usePublicFinishTypes } from '../../hooks/usePublicFinishTypes';
import { vases } from '../../data/vases';
import type { ColourFinish, RequestItem, SelectedVase } from '../../types';
import { REQUEST_FLOW_FIELD_IDS } from '../../lib/flowFieldExtensions';
import { asRequestItemList, asSelectionContext } from '../../lib/flowNodeListHelpers';
import type { CanvasSectionBlock } from '../sections/sectionRegistry';
import RequestItemsPanel from './RequestItemsPanel';
import { findFlowNodeIdByType, removeRequestItemFromFlow } from './requestItemFlowActions';
import { buildSocketPatch, useConnectedBlockRuntime } from './connectedBlockRuntime';
import { resolveFlowNodeSockets } from '../../lib/flowNodeDefinitions';
import { VASE_FLOW_FIELD_IDS } from '../../lib/flows';
import {
  findProductRecordById,
  resolveDefaultColourwayOption,
  resolveProductRecordModel,
  resolveProductRecordModelPath,
  resolveProductRecordTitle,
} from '../../lib/productRecords';
import { scrollToSectionAnchor } from '../../lib/sectionAnchors';
import {
  buttonVisualStateToCssDeclarations,
  buttonVisualStateToReactStyle,
  normalizeButtonDesignStyle,
} from '../../lib/buttonDesignStyles';
import { resolveViewerSurfacePresentation } from '../../lib/viewerSurfaceStyles';
import { PUBLIC_VIEWER_ENGINE } from '../../lib/productViewerEngine';

const ModelViewer = React.lazy(() => import('../ModelViewer'));
const VaseStage3D = React.lazy(() =>
  import('../vaseStage3d').then((module) => ({ default: module.VaseStage3D }))
);

const VIEWER_EMPTY_ACTION_POSITIONS = new Set(['above', 'below', 'left', 'right']);
const VIEWER_EMPTY_ACTION_ALIGNS = new Set(['start', 'center', 'end']);

function resolveViewerEmptyActionPosition(value: unknown) {
  return typeof value === 'string' && VIEWER_EMPTY_ACTION_POSITIONS.has(value) ? value : 'below';
}

function resolveViewerEmptyActionAlign(value: unknown) {
  return typeof value === 'string' && VIEWER_EMPTY_ACTION_ALIGNS.has(value) ? value : 'center';
}

function resolveFiniteNumber(value: unknown, fallback: number) {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

type ConnectedBlockSurfaceProps = {
  block: CanvasSectionBlock;
  sectionId?: string | null;
  isAdmin?: boolean;
  isInteractPreview?: boolean;
  selectionOverlay?: React.ReactNode;
  onDebugSnapshot?: (blockId: string, snapshot: Record<string, unknown> | null) => void;
  fontLibrary?: Record<string, string>;
};

type ConnectedBlockRuntimeHandle = ReturnType<typeof useConnectedBlockRuntime>;

function emitConnectedBlockTrigger(
  runtime: ConnectedBlockRuntimeHandle,
  outputSocketId: string
) {
  if (!runtime.flowId || !runtime.summary) return;

  const activeFlow = runtime.flowDefinition ?? runtime.summary.flow;
  const fieldId = runtime.getSocketFieldId('output', outputSocketId);
  if (!fieldId) return;

  runtime.patchFlowValues(runtime.flowId, { [fieldId]: Date.now() });
  activeFlow.nodes
    .filter((candidate) => candidate.id !== runtime.summary?.node.id)
    .filter((candidate) =>
      candidate.inputs.some((binding) => {
        if (binding.fieldId !== fieldId) return false;
        const socketDefinition = resolveFlowNodeSockets(candidate, activeFlow).find(
          (socket) => socket.direction === 'input' && socket.id === binding.socketId
        );
        return socketDefinition?.portKind === 'trigger';
      })
    )
    .forEach((candidate) => {
      runtime.invokeFlowNode(runtime.flowId!, candidate.id);
    });
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function isSelectedVase(value: unknown): value is SelectedVase {
  const record = asRecord(value);
  return Boolean(record) && Number.isFinite(record.id) && typeof record.name === 'string';
}

function parseColourNames(colorDescription: string | null | undefined): string[] {
  if (!colorDescription) return [];
  return colorDescription
    .split('/')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function resolveColourSwatchMap(combinations: ColourCombination[]): Record<string, string> {
  const next: Record<string, string> = {};
  combinations.forEach((combo) => {
    const names = parseColourNames(combo.color_description);
    names.forEach((name, index) => {
      const swatch = combo.colours?.[index];
      if (!next[name] && typeof swatch === 'string' && swatch.trim().length > 0) {
        next[name] = swatch.trim();
      }
    });
  });
  return next;
}

function resolveProductModel(
  selectedProduct: SelectedVase | null,
  managedProducts: SelectedVase[]
) {
  if (!selectedProduct) return null;
  const managedProduct = findProductRecordById(managedProducts, selectedProduct.id) ?? selectedProduct;
  const catalogVase = resolveProductRecordModel(managedProduct) ?? vases.find((entry) => entry.id === selectedProduct.id) ?? null;
  const modelPath = resolveProductRecordModelPath(managedProduct) || catalogVase?.modelPath;
  if (!modelPath) return null;
  return {
    modelPath,
    vaseId:
      Number.isFinite(managedProduct.modelId) && Number(managedProduct.modelId) > 0
        ? Number(managedProduct.modelId)
        : selectedProduct.id,
    title: resolveProductRecordTitle(managedProduct),
  };
}

function formatProductPrice(value: number | null | undefined) {
  if (!Number.isFinite(value)) return 'Waiting for pricing';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: Math.round((value ?? 0) * 100) % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value as number);
}

function buildCustomiserCanonicalResetPatch(args: {
  clearColours?: boolean;
  clearCombination?: boolean;
}) {
  const patch: Record<string, unknown> = {};
  if (args.clearColours) {
    patch[VASE_FLOW_FIELD_IDS.chosenColours] = [];
  }
  if (args.clearCombination) {
    patch[VASE_FLOW_FIELD_IDS.chosenCombination] = null;
  }
  return patch;
}

function getBuilderInteractionProps(allowInteraction: boolean) {
  if (!allowInteraction) return {};
  return {
    'data-builder-allow-interaction': 'true' as const,
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      event.stopPropagation();
    },
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
    },
  };
}

function asColourFinish(value: unknown): ColourFinish | null {
  return typeof value === 'string' && value.trim().length > 0
    ? (value as ColourFinish)
    : null;
}

function flowValueEquals(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function getCanonicalCustomiserValue(
  runtime: Pick<ReturnType<typeof useConnectedBlockRuntime>, 'flowId' | 'getFlowValue'>,
  fieldId: string
) {
  return runtime.flowId ? runtime.getFlowValue(runtime.flowId, fieldId) : undefined;
}

function resolveCustomiserSelectedProduct(
  runtime: Pick<
    ReturnType<typeof useConnectedBlockRuntime>,
    'getSocketValue' | 'flowId' | 'getFlowValue'
  >
) {
  const directProduct = runtime.getSocketValue('input', 'product');
  if (isSelectedVase(directProduct)) {
    return directProduct as SelectedVase;
  }

  const currentSelection = asSelectionContext(runtime.getSocketValue('input', 'selection'));
  if (isSelectedVase(currentSelection?.product)) {
    return currentSelection.product as SelectedVase;
  }

  const canonicalProduct = getCanonicalCustomiserValue(runtime, VASE_FLOW_FIELD_IDS.chosenProduct);
  return isSelectedVase(canonicalProduct) ? (canonicalProduct as SelectedVase) : null;
}

function resolveCustomiserSelection(
  runtime: Pick<
    ReturnType<typeof useConnectedBlockRuntime>,
    'getSocketValue'
  >
) {
  return asSelectionContext(runtime.getSocketValue('input', 'selection'));
}

function resolveCustomiserSelectedFinish(
  runtime: Pick<
    ReturnType<typeof useConnectedBlockRuntime>,
    'getSocketValue' | 'flowId' | 'getFlowValue'
  >
) {
  const directFinish = asColourFinish(runtime.getSocketValue('input', 'finish'));
  if (directFinish) return directFinish;

  const currentSelection = resolveCustomiserSelection(runtime);
  const selectionFinish = asColourFinish(currentSelection?.finish);
  if (selectionFinish) return selectionFinish;

  return asColourFinish(getCanonicalCustomiserValue(runtime, VASE_FLOW_FIELD_IDS.chosenFinish));
}

function resolveCustomiserSelectedColours(
  runtime: Pick<
    ReturnType<typeof useConnectedBlockRuntime>,
    'getSocketValue' | 'flowId' | 'getFlowValue'
  >,
  direction: 'input' | 'output'
) {
  const directColours = runtime.getSocketValue(direction, 'colours');
  if (Array.isArray(directColours)) {
    return directColours.filter((entry): entry is string => typeof entry === 'string');
  }

  const currentSelection = resolveCustomiserSelection(runtime);
  if (Array.isArray(currentSelection?.colours)) {
    return currentSelection.colours.filter((entry): entry is string => typeof entry === 'string');
  }

  const canonicalColours = getCanonicalCustomiserValue(runtime, VASE_FLOW_FIELD_IDS.chosenColours);
  return Array.isArray(canonicalColours)
    ? canonicalColours.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

function toDebugValue(value: unknown): unknown {
  if (value === undefined) return null;
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
    };
  }
  return value;
}

function buildRuntimeSocketSnapshot(
  runtime: ReturnType<typeof useConnectedBlockRuntime>,
  direction: 'input' | 'output'
) {
  const sockets = direction === 'input' ? runtime.summary?.inputs : runtime.summary?.outputs;
  return (sockets ?? []).map((entry) => ({
    socketId: entry.socket.id,
    label: entry.socket.label,
    fieldLabel: entry.fieldLabel,
    fieldType: entry.fieldType,
    fieldId: runtime.getSocketFieldId(direction, entry.socket.id),
    value: toDebugValue(runtime.getSocketValue(direction, entry.socket.id)),
  }));
}

function useConnectedBlockDebugSnapshot(
  block: CanvasSectionBlock,
  runtime: ReturnType<typeof useConnectedBlockRuntime>,
  details: Record<string, unknown>,
  onDebugSnapshot?: (blockId: string, snapshot: Record<string, unknown> | null) => void
) {
  const snapshot = useMemo(
    () => ({
      blockId: block.id,
      blockType: block.type,
      flowId: runtime.flowId,
      nodeId: runtime.summary?.node.id ?? null,
      sockets: {
        inputs: buildRuntimeSocketSnapshot(runtime, 'input'),
        outputs: buildRuntimeSocketSnapshot(runtime, 'output'),
      },
      details,
    }),
    [block.id, block.type, details, runtime]
  );
  const snapshotKey = useMemo(() => JSON.stringify(snapshot), [snapshot]);
  const lastReportedSnapshotRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onDebugSnapshot) return;
    if (lastReportedSnapshotRef.current === snapshotKey) return;
    lastReportedSnapshotRef.current = snapshotKey;
    onDebugSnapshot(block.id, snapshot);
  }, [block.id, onDebugSnapshot, snapshot, snapshotKey]);

  useEffect(
    () => () => {
      if (!onDebugSnapshot) return;
      onDebugSnapshot(block.id, null);
    },
    [block.id, onDebugSnapshot]
  );
}

type ViewerMode = 'fixed' | 'randomOnLoad' | 'controlled';

function normalizeViewerMode(value: unknown): ViewerMode {
  return value === 'fixed' || value === 'randomOnLoad' ? value : 'controlled';
}

const ViewerSurface: React.FC<ConnectedBlockSurfaceProps> = ({
  block,
  sectionId,
  isAdmin = false,
  isInteractPreview = false,
  selectionOverlay,
  onDebugSnapshot,
  fontLibrary,
}) => {
  const runtime = useConnectedBlockRuntime(block, { sectionId });
  const { products: managedProducts } = useProductRecords({ admin: isAdmin });
  const { combinations: colourwayCatalog } = useColourCombinationCatalog({ includeHidden: isAdmin });
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [viewerResetKey, setViewerResetKey] = useState(0);
  const viewerContent = asRecord(block.content) ?? {};
  const [randomProduct] = useState<SelectedVase | null>(() => {
    if (vases.length === 0) return null;
    const next = vases[Math.floor(Math.random() * vases.length)] ?? null;
    return next
      ? {
          id: next.id,
          name: next.name,
          modelPath: next.modelPath,
        }
      : null;
  });
  const selection = asSelectionContext(runtime.getSocketValue('input', 'selection'));
  const explicitProduct = isSelectedVase(runtime.getSocketValue('input', 'product'))
    ? (runtime.getSocketValue('input', 'product') as SelectedVase)
    : null;
  const explicitCombination = asRecord(runtime.getSocketValue('input', 'combination'));
  const viewerMode = normalizeViewerMode(
    runtime.getSocketValue('input', 'viewerMode') ??
      (block.content as Record<string, unknown> | undefined)?.viewerMode
  );
  const selectedProduct =
    explicitProduct ??
    selection?.product ??
    (viewerMode === 'randomOnLoad' ? randomProduct : null);
  const lastResolvedProductRef = useRef<SelectedVase | null>(null);
  if (selectedProduct) {
    lastResolvedProductRef.current = selectedProduct;
  }
  const explicitFinish = asColourFinish(runtime.getSocketValue('input', 'finish'));
  const selectionFinish = asColourFinish(selection?.finish);
  const { finishOptions } = usePublicFinishTypes();
  const selectionColours = Array.isArray(selection?.colours)
    ? selection.colours.filter((entry): entry is string => typeof entry === 'string')
    : [];
  const requestedCombination = explicitCombination ?? asRecord(selection?.combination);
  const stableSelectedProduct =
    selectedProduct ??
    (explicitFinish || selectionFinish || selectionColours.length > 0 || requestedCombination
      ? lastResolvedProductRef.current
      : null);
  const stableManagedProduct =
    (stableSelectedProduct
      ? findProductRecordById(managedProducts, stableSelectedProduct.id)
      : null) ?? stableSelectedProduct;
  const requestedCombinationId =
    typeof requestedCombination?.id === 'string' && requestedCombination.id.trim().length > 0
      ? requestedCombination.id.trim()
      : null;
  const matchedRequestedCombination = requestedCombinationId
    ? colourwayCatalog.find((entry) => entry.id === requestedCombinationId) ?? null
    : null;
  const chosenFinish = explicitFinish ?? selectionFinish;
  const finishDefaultColourwayId = chosenFinish
    ? finishOptions.find((option) => option.value === chosenFinish)?.defaultColourwayId ?? null
    : null;
  const finishDefaultCombination = finishDefaultColourwayId
    ? colourwayCatalog.find((entry) => entry.id === finishDefaultColourwayId) ?? null
    : null;
  const firstMatchingFinishCombination = chosenFinish
    ? colourwayCatalog.find((entry) => entry.finish_type === chosenFinish) ?? null
    : null;
  const fallbackCombination =
    chosenFinish
      ? finishDefaultCombination ?? firstMatchingFinishCombination
      : resolveDefaultColourwayOption(stableManagedProduct, colourwayCatalog) ?? null;
  const shouldUseFallbackCombination =
    !requestedCombination &&
    selectionColours.length === 0;
  const selectedCombination =
    matchedRequestedCombination ??
    (requestedCombination && !requestedCombinationId ? requestedCombination : null) ??
    (shouldUseFallbackCombination ? fallbackCombination : null);
  const selectedFinish = String(
    explicitFinish ??
      selectedCombination?.finish_type ??
      selectionFinish ??
      'shifting'
  ) as ColourFinish;
  const selectedColours = Array.isArray(selectedCombination?.colours)
    ? selectedCombination.colours.filter((entry): entry is string => typeof entry === 'string')
    : selectionColours.length > 0
      ? selectionColours
      : [];
  const productModel = resolveProductModel(stableSelectedProduct, managedProducts);
  const viewerTitle =
    typeof (block.content as Record<string, unknown> | undefined)?.title === 'string'
      ? String((block.content as Record<string, unknown>).title)
      : 'Viewer Control';
  const emptyStateCopy =
    typeof (block.content as Record<string, unknown> | undefined)?.emptyState === 'string'
      ? String((block.content as Record<string, unknown>).emptyState)
      : 'Choose a product to start customising.';
  const emptyStateActionLabel =
    typeof (block.content as Record<string, unknown> | undefined)?.emptyStateActionLabel === 'string'
      ? String((block.content as Record<string, unknown>).emptyStateActionLabel).trim()
      : '';
  const emptyStateScrollTargetId =
    typeof (block.content as Record<string, unknown> | undefined)?.emptyStateScrollTargetId === 'string'
      ? String((block.content as Record<string, unknown>).emptyStateScrollTargetId).trim()
      : '';
  const emptyStateActionPosition = resolveViewerEmptyActionPosition(
    (block.content as Record<string, unknown> | undefined)?.emptyStateActionPosition
  );
  const emptyStateActionAlign = resolveViewerEmptyActionAlign(
    (block.content as Record<string, unknown> | undefined)?.emptyStateActionAlign
  );
  const emptyStateActionGap = Math.max(
    0,
    Math.min(80, resolveFiniteNumber((block.content as Record<string, unknown> | undefined)?.emptyStateActionGap, 12))
  );
  const emptyStateActionOffsetX = Math.max(
    -240,
    Math.min(240, resolveFiniteNumber((block.content as Record<string, unknown> | undefined)?.emptyStateActionOffsetX, 0))
  );
  const emptyStateActionOffsetY = Math.max(
    -240,
    Math.min(240, resolveFiniteNumber((block.content as Record<string, unknown> | undefined)?.emptyStateActionOffsetY, 0))
  );
  const emptyStateTextStyle = useMemo(
    () => buttonVisualStateToReactStyle(normalizeButtonDesignStyle(block.style), fontLibrary),
    [block.style, fontLibrary]
  );
  const emptyStateActionDesign = useMemo(
    () =>
      normalizeButtonDesignStyle(
        (block.content as Record<string, unknown> | undefined)?.emptyStateActionStyle
      ),
    [block.content]
  );
  const emptyStateActionStyle = useMemo(
    () => buttonVisualStateToReactStyle(emptyStateActionDesign, fontLibrary),
    [emptyStateActionDesign, fontLibrary]
  );
  const emptyStateActionHoverCss = useMemo(
    () => buttonVisualStateToCssDeclarations(emptyStateActionDesign.hover, fontLibrary),
    [emptyStateActionDesign.hover, fontLibrary]
  );
  const allowInteraction = !isAdmin || isInteractPreview;
  const interactionProps = getBuilderInteractionProps(allowInteraction);
  const openViewerSignal = runtime.getSocketValue('input', 'openViewer');
  const closeViewerSignal = runtime.getSocketValue('input', 'closeViewer');
  const resetViewSignal = runtime.getSocketValue('input', 'resetView');
  const cameraPreset =
    typeof runtime.getSocketValue('input', 'cameraPreset') === 'string'
      ? String(runtime.getSocketValue('input', 'cameraPreset'))
      : typeof (block.content as Record<string, unknown> | undefined)?.cameraPreset === 'string'
        ? String((block.content as Record<string, unknown>).cameraPreset)
        : '';
  const previousOpenSignalRef = useRef(openViewerSignal);
  const previousCloseSignalRef = useRef(closeViewerSignal);
  const previousResetSignalRef = useRef(resetViewSignal);
  const previousCameraPresetRef = useRef(cameraPreset);
  const previousFullscreenRef = useRef(isFullscreenOpen);
  const previousModelKeyRef = useRef<string | null>(null);

  const emitViewerTrigger = useCallback(
    (socketId: 'onViewerOpened' | 'onViewerClosed' | 'onModelLoaded') => {
      if (!runtime.flowId || !runtime.summary) return;
      const fieldId = runtime.getSocketFieldId('output', socketId);
      if (!fieldId) return;
      const pulseValue = Date.now();
      runtime.patchFlowValues(runtime.flowId, { [fieldId]: pulseValue });
      runtime.summary.flow.nodes
        .filter((candidate) => candidate.id !== runtime.summary?.node.id)
        .filter((candidate) =>
          candidate.inputs.some((binding) => {
            if (binding.fieldId !== fieldId) return false;
            const socketDefinition = resolveFlowNodeSockets(candidate, runtime.summary.flow).find(
              (socket) => socket.direction === 'input' && socket.id === binding.socketId
            );
            return socketDefinition?.portKind === 'trigger';
          })
        )
        .forEach((candidate) => {
          runtime.invokeFlowNode(runtime.flowId!, candidate.id);
        });
    },
    [runtime]
  );

  useEffect(() => {
    if (openViewerSignal !== previousOpenSignalRef.current) {
      previousOpenSignalRef.current = openViewerSignal;
      if (openViewerSignal != null) {
        setIsFullscreenOpen(true);
      }
    }
  }, [openViewerSignal]);

  useEffect(() => {
    if (closeViewerSignal !== previousCloseSignalRef.current) {
      previousCloseSignalRef.current = closeViewerSignal;
      if (closeViewerSignal != null) {
        setIsFullscreenOpen(false);
      }
    }
  }, [closeViewerSignal]);

  useEffect(() => {
    if (resetViewSignal !== previousResetSignalRef.current) {
      previousResetSignalRef.current = resetViewSignal;
      if (resetViewSignal != null) {
        setViewerResetKey((current) => current + 1);
      }
    }
  }, [resetViewSignal]);

  useEffect(() => {
    if (cameraPreset !== previousCameraPresetRef.current) {
      previousCameraPresetRef.current = cameraPreset;
      setViewerResetKey((current) => current + 1);
    }
  }, [cameraPreset]);

  useEffect(() => {
    if (!runtime.flowId) return;
    const nextActiveProduct = stableManagedProduct ?? stableSelectedProduct;
    const nextActiveFinish = selectedFinish;
    const nextActiveCombination = selectedCombination;
    const patch = buildSocketPatch(
      runtime,
      [
        {
          direction: 'output',
          socketId: 'activeProduct',
          value: nextActiveProduct,
        },
        {
          direction: 'output',
          socketId: 'activeFinish',
          value: nextActiveFinish,
        },
        {
          direction: 'output',
          socketId: 'activeCombination',
          value: nextActiveCombination,
        },
      ].filter((entry) => {
        const currentValue = runtime.getSocketValue(entry.direction, entry.socketId);
        return !flowValueEquals(currentValue, entry.value);
      })
    );
    if (Object.keys(patch).length > 0) {
      runtime.patchFlowValues(runtime.flowId, patch);
    }
  }, [runtime, selectedCombination, selectedFinish, stableManagedProduct, stableSelectedProduct]);

  useEffect(() => {
    if (previousFullscreenRef.current === isFullscreenOpen) return;
    previousFullscreenRef.current = isFullscreenOpen;
    emitViewerTrigger(isFullscreenOpen ? 'onViewerOpened' : 'onViewerClosed');
  }, [emitViewerTrigger, isFullscreenOpen]);

  useEffect(() => {
    const modelKey = productModel ? `${productModel.vaseId}:${productModel.modelPath}` : null;
    if (!modelKey || modelKey === previousModelKeyRef.current) return;
    previousModelKeyRef.current = modelKey;
    emitViewerTrigger('onModelLoaded');
  }, [emitViewerTrigger, productModel]);

  /** Remount canvas on model identity only — colour/finish updates stay in-place on HeroVase. */
  const viewerMountKey = productModel
    ? `${productModel.vaseId}:${productModel.modelPath}`
    : 'none';
  const inlineViewerSurface = useMemo(
    () => resolveViewerSurfacePresentation(viewerContent, 'customiser-inline'),
    [viewerContent]
  );
  const fullscreenViewerSurface = useMemo(
    () => resolveViewerSurfacePresentation(viewerContent, 'customiser-fullscreen'),
    [viewerContent]
  );
  const viewerDebugDetails = useMemo(
    () => ({
      viewerMode,
      explicitProduct: toDebugValue(explicitProduct),
      selectionProduct: toDebugValue(selection?.product ?? null),
      selectedProduct: toDebugValue(selectedProduct),
      stableSelectedProduct: toDebugValue(stableSelectedProduct),
      stableManagedProduct: toDebugValue(stableManagedProduct),
      explicitFinish,
      selectionFinish,
      selectedFinish,
      selectionColours,
      selectedColours,
      requestedCombination: toDebugValue(requestedCombination),
      selectedCombination: toDebugValue(selectedCombination),
      productModel: toDebugValue(productModel),
      emptyStateVisible: !productModel,
      isFullscreenOpen,
      cameraPreset,
    }),
    [
      cameraPreset,
      explicitProduct,
      isFullscreenOpen,
      productModel,
      requestedCombination,
      selectedColours,
      selectedCombination,
      selectedFinish,
      selectedProduct,
      selection,
      selectionColours,
      selectionFinish,
      stableManagedProduct,
      stableSelectedProduct,
      viewerMode,
    ]
  );
  useConnectedBlockDebugSnapshot(block, runtime, viewerDebugDetails, onDebugSnapshot);

  const renderViewer = (fullscreen: boolean) => {
    const surfacePresentation = fullscreen ? fullscreenViewerSurface : inlineViewerSurface;
    if (!productModel) {
      const canScrollFromEmptyState = emptyStateScrollTargetId.length > 0 && allowInteraction;
      const actionDomId = `viewer-empty-action-${block.id}`;
      const stackDirection =
        emptyStateActionPosition === 'left' || emptyStateActionPosition === 'right' ? 'row' : 'column';
      const actionOrder = emptyStateActionPosition === 'above' || emptyStateActionPosition === 'left' ? -1 : 1;
      const stackAlignClass =
        emptyStateActionAlign === 'start'
          ? 'items-start text-left'
          : emptyStateActionAlign === 'end'
            ? 'items-end text-right'
            : 'items-center text-center';
      return (
        <div
          className={`flex h-full items-center justify-center px-6 text-center text-sm ${
            surfacePresentation.tone === 'dark' ? 'text-white/72' : 'text-stone-500'
          }`}
        >
          {emptyStateScrollTargetId ? (
            <div
              className={`inline-flex max-w-full ${stackAlignClass}`}
              style={{
                flexDirection: stackDirection,
                gap: `${emptyStateActionGap}px`,
                transform: `translate(${emptyStateActionOffsetX}px, ${emptyStateActionOffsetY}px)`,
              }}
            >
              <span style={emptyStateTextStyle}>{emptyStateCopy}</span>
              {emptyStateActionLabel ? (
                <>
                  {emptyStateActionHoverCss ? (
                    <style>{`[data-viewer-empty-action-id="${actionDomId}"]:hover { ${emptyStateActionHoverCss} }`}</style>
                  ) : null}
                  <button
                    type="button"
                    data-viewer-empty-action-id={actionDomId}
                    disabled={!allowInteraction}
                    onClick={() => {
                      if (!canScrollFromEmptyState) return;
                      scrollToSectionAnchor(emptyStateScrollTargetId);
                    }}
                    style={{
                      ...emptyStateActionStyle,
                      order: actionOrder,
                    }}
                    className="inline-flex rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700 shadow-sm transition hover:border-stone-400 hover:bg-stone-50 disabled:cursor-default"
                  >
                  {emptyStateActionLabel}
                  </button>
                </>
              ) : null}
            </div>
          ) : (
            <span style={emptyStateTextStyle}>{emptyStateCopy}</span>
          )}
        </div>
      );
    }

    const viewerSuspenseFallback = (
      <div
        className={`flex h-full items-center justify-center text-sm font-medium ${
          surfacePresentation.tone === 'dark' ? 'text-white/80' : 'text-stone-500'
        }`}
      >
        Loading 3D preview...
      </div>
    );

    return (
      <Suspense fallback={viewerSuspenseFallback}>
        {PUBLIC_VIEWER_ENGINE === 'vaseStage3d' ? (
          <VaseStage3D
            key={`${fullscreen ? 'fullscreen' : 'inline'}:${viewerMountKey}`}
            profile="product"
            model={{
              modelPath: productModel.modelPath,
              vaseId: productModel.vaseId,
              selectedColors: selectedColours,
              finishType: selectedFinish,
            }}
            interaction={{
              rotationResetKey: viewerResetKey,
              enableDrag: allowInteraction,
              enableZoom: false,
            }}
            container={{
              className: 'h-full w-full',
              transparentBackground: surfacePresentation.transparentCanvas,
              backgroundColor: surfacePresentation.canvasColor,
            }}
            adminPerformanceMode={isAdmin && !isInteractPreview}
          />
        ) : (
          <ModelViewer
            key={`${fullscreen ? 'fullscreen' : 'inline'}:${viewerMountKey}:${selectedFinish}:${cameraPreset}:${viewerResetKey}`}
            modelPath={productModel.modelPath}
            vaseId={productModel.vaseId}
            selectedColors={selectedColours}
            finishType={selectedFinish}
            isPreview
            publicLightingMode="product"
            backgroundColor={surfacePresentation.canvasColor}
            interactionMode="turntable"
            enableZoom={allowInteraction}
            transparentBackground={surfacePresentation.transparentCanvas}
            containerClassName="h-full w-full"
          />
        )}
      </Suspense>
    );
  };

  return (
    <>
      <div
        className="relative h-full w-full overflow-hidden rounded-[24px] border border-stone-200 bg-white shadow-sm"
        {...interactionProps}
      >
        <div className="flex items-center justify-between gap-2 border-b border-stone-200/80 px-4 py-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
              {viewerTitle}
            </div>
            <h3 className="text-sm font-semibold text-stone-900">
              {resolveProductRecordTitle(stableManagedProduct ?? stableSelectedProduct) || 'Select a vase to begin'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {selectedCombination?.color_description ? (
              <span className="rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-medium text-stone-700">
                {String(selectedCombination.color_description)}
              </span>
            ) : null}
            {allowInteraction && productModel ? (
              <button
                type="button"
                className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-[11px] font-medium text-stone-700 transition hover:border-stone-300 hover:bg-stone-50"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsFullscreenOpen(true);
                }}
              >
                View in 3D
              </button>
            ) : null}
          </div>
        </div>
        <div
          className="relative h-[calc(100%-57px)]"
          style={inlineViewerSurface.shellStyle}
          data-builder-allow-interaction={allowInteraction ? 'true' : undefined}
        >
          {renderViewer(false)}
        </div>
        {selectionOverlay}
      </div>
      {isFullscreenOpen && productModel ? (
        <div
          className="fixed inset-0 z-[140] flex items-center justify-center bg-stone-950/88 p-6"
          data-builder-allow-interaction
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setIsFullscreenOpen(false);
          }}
        >
          <div
            className="relative h-full max-h-[92vh] w-full max-w-7xl overflow-hidden rounded-[28px] border border-white/10 bg-stone-950 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 text-white">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/55">
                  Fullscreen Viewer
                </div>
                <div className="text-sm font-semibold">
                  {resolveProductRecordTitle(stableManagedProduct ?? stableSelectedProduct) || 'Viewer'}
                </div>
              </div>
              <button
                type="button"
                className="rounded-full border border-white/15 px-3 py-1.5 text-xs font-medium text-white transition hover:border-white/30 hover:bg-white/10"
                onClick={() => setIsFullscreenOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="h-[calc(100%-69px)]" style={fullscreenViewerSurface.shellStyle}>
              {renderViewer(true)}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

const ProductDeconstructorSurface: React.FC<ConnectedBlockSurfaceProps> = ({
  block,
  sectionId,
  isAdmin = false,
  isInteractPreview = false,
  selectionOverlay,
  onDebugSnapshot,
}) => {
  const runtime = useConnectedBlockRuntime(block, { sectionId });
  const selectedProduct = isSelectedVase(runtime.getSocketValue('input', 'product'))
    ? (runtime.getSocketValue('input', 'product') as SelectedVase)
    : null;
  const catalogProduct = selectedProduct ? vases.find((entry) => entry.id === selectedProduct.id) ?? null : null;
  const blockContent = asRecord(block.content);
  const resolvedName = catalogProduct?.name ?? selectedProduct?.name ?? null;
  const resolvedMaterial =
    (typeof blockContent?.material === 'string' && blockContent.material.trim().length > 0
      ? blockContent.material.trim()
      : '3D Printed PLA');
  const resolvedPrice =
    Number.isFinite(blockContent?.price) ? Number(blockContent?.price) : 85;
  const allowInteraction = !isAdmin || isInteractPreview;
  const interactionProps = getBuilderInteractionProps(allowInteraction);

  useEffect(() => {
    if (!runtime.flowId) return;
    const nameFieldId = runtime.getSocketFieldId('output', 'name');
    const priceFieldId = runtime.getSocketFieldId('output', 'price');
    const materialFieldId = runtime.getSocketFieldId('output', 'material');
    const nextName = resolvedName ?? undefined;
    const nextPrice = selectedProduct ? resolvedPrice : undefined;
    const nextMaterial = selectedProduct ? resolvedMaterial : undefined;
    const patch: Record<string, unknown> = {};

    if (nameFieldId && runtime.getSocketValue('output', 'name') !== nextName) {
      patch[nameFieldId] = nextName;
    }
    if (priceFieldId && runtime.getSocketValue('output', 'price') !== nextPrice) {
      patch[priceFieldId] = nextPrice;
    }
    if (materialFieldId && runtime.getSocketValue('output', 'material') !== nextMaterial) {
      patch[materialFieldId] = nextMaterial;
    }

    if (Object.keys(patch).length > 0) {
      runtime.patchFlowValues(runtime.flowId, patch);
    }
  }, [
    resolvedMaterial,
    resolvedName,
    resolvedPrice,
    runtime,
    selectedProduct,
  ]);

  const deconstructorDebugDetails = useMemo(
    () => ({
      selectedProduct: toDebugValue(selectedProduct),
      resolvedName,
      resolvedPrice,
      resolvedMaterial,
    }),
    [resolvedMaterial, resolvedName, resolvedPrice, selectedProduct]
  );
  useConnectedBlockDebugSnapshot(block, runtime, deconstructorDebugDetails, onDebugSnapshot);

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-[24px] border border-sky-200/70 bg-[linear-gradient(180deg,#f8fbff_0%,#eef6ff_100%)] shadow-sm"
      {...interactionProps}
    >
      <div className="border-b border-sky-100/90 px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-600/75">
          Product Deconstructor
        </div>
        <h3 className="text-sm font-semibold text-slate-900">Metadata Outputs</h3>
      </div>
      <div className="grid gap-2 p-3 sm:grid-cols-3">
        {[
          ['Name', resolvedName ?? 'Waiting for product'],
          ['Price', selectedProduct ? formatProductPrice(resolvedPrice) : 'Waiting for product'],
          ['Material', selectedProduct ? resolvedMaterial : 'Waiting for product'],
        ].map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-sky-100 bg-white/85 px-3 py-2 shadow-[0_8px_24px_rgba(148,163,184,0.12)]">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
            <div className="mt-1 text-sm font-medium text-slate-900">{value}</div>
          </div>
        ))}
      </div>
      {selectionOverlay}
    </div>
  );
};

const FinishPickerSurface: React.FC<ConnectedBlockSurfaceProps> = ({
  block,
  sectionId,
  isAdmin = false,
  isInteractPreview = false,
  selectionOverlay,
  onDebugSnapshot,
}) => {
  const runtime = useConnectedBlockRuntime(block, { sectionId });
  const { finishOptions } = usePublicFinishTypes();
  const selectedProduct = resolveCustomiserSelectedProduct(runtime);
  const selectedFinish = asColourFinish(
    runtime.getSocketValue('output', 'finish') ??
      getCanonicalCustomiserValue(runtime, VASE_FLOW_FIELD_IDS.chosenFinish)
  );
  const allowInteraction = Boolean((!isAdmin || isInteractPreview) && runtime.flowId && selectedProduct);
  const interactionProps = getBuilderInteractionProps(allowInteraction);
  const finishPickerDebugDetails = useMemo(
    () => ({
      selectedProduct: toDebugValue(selectedProduct),
      selectedFinish,
      finishOptionCount: finishOptions.length,
      allowInteraction,
    }),
    [allowInteraction, finishOptions.length, selectedFinish, selectedProduct]
  );
  useConnectedBlockDebugSnapshot(block, runtime, finishPickerDebugDetails, onDebugSnapshot);

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-[24px] border border-stone-200 bg-white shadow-sm"
      {...interactionProps}
    >
      <div className="border-b border-stone-200/80 px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
          Finish Style
        </div>
        <h3 className="text-sm font-semibold text-stone-900">Choose a finish</h3>
      </div>
      <div className="space-y-2 p-3">
        {finishOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={!allowInteraction}
            onClick={() => {
              if (!runtime.flowId) return;
              const patch =
                buildSocketPatch(runtime, [
                { direction: 'output', socketId: 'finish', value: option.value },
              ]) || {};
              const resolvedPatch = {
                ...(Object.keys(patch).length > 0
                  ? patch
                  : { [VASE_FLOW_FIELD_IDS.chosenFinish]: option.value }),
                ...buildCustomiserCanonicalResetPatch({
                  clearColours: true,
                  clearCombination: true,
                }),
              };
              runtime.patchFlowValues(runtime.flowId, resolvedPatch);
              emitConnectedBlockTrigger(runtime, 'finishSelected');
            }}
            className={`w-full rounded-2xl border px-3 py-2 text-left transition ${
              selectedFinish === option.value
                ? 'border-stone-900 bg-stone-900 text-white'
                : 'border-stone-200 bg-stone-50 text-stone-700'
            } ${allowInteraction ? 'hover:border-stone-400 hover:bg-stone-100' : 'cursor-default opacity-80'}`}
          >
            <div className="text-sm font-semibold">{option.label}</div>
            <div className={`mt-1 text-xs leading-5 ${selectedFinish === option.value ? 'text-white/80' : 'text-stone-500'}`}>
              {option.description}
            </div>
          </button>
        ))}
        {!selectedProduct ? (
          <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-500">
            Select a vase first to activate the finish controls.
          </div>
        ) : null}
      </div>
      {selectionOverlay}
    </div>
  );
};

const ColourFilterSurface: React.FC<ConnectedBlockSurfaceProps> = ({
  block,
  sectionId,
  isAdmin = false,
  isInteractPreview = false,
  selectionOverlay,
  onDebugSnapshot,
}) => {
  const runtime = useConnectedBlockRuntime(block, { sectionId });
  const selectedProduct = resolveCustomiserSelectedProduct(runtime);
  const selectedFinish = resolveCustomiserSelectedFinish(runtime);
  const selectedColours = resolveCustomiserSelectedColours(runtime, 'output');
  const { combinations, availableColours, loading, error, setSelectedFinish } = useColours();
  const swatchMap = useMemo(() => resolveColourSwatchMap(combinations), [combinations]);
  const colourOptions = useMemo(
    () => Array.from(new Set([...selectedColours, ...availableColours(selectedColours)])),
    [availableColours, selectedColours]
  );
  const allowInteraction = Boolean(
    (!isAdmin || isInteractPreview) && runtime.flowId && selectedProduct && selectedFinish
  );
  const interactionProps = getBuilderInteractionProps(allowInteraction);

  useEffect(() => {
    if (!selectedFinish) return;
    setSelectedFinish(selectedFinish);
  }, [selectedFinish, setSelectedFinish]);

  const colourFilterDebugDetails = useMemo(
    () => ({
      selectedProduct: toDebugValue(selectedProduct),
      selectedFinish,
      selectedColours,
      colourOptions,
      availableColourCount: colourOptions.length,
      combinationCount: combinations.length,
      allowInteraction,
      loading,
      error: error ?? null,
    }),
    [
      allowInteraction,
      colourOptions,
      combinations.length,
      error,
      loading,
      selectedColours,
      selectedFinish,
      selectedProduct,
    ]
  );
  useConnectedBlockDebugSnapshot(block, runtime, colourFilterDebugDetails, onDebugSnapshot);

  const toggleColour = (colour: string) => {
    if (!runtime.flowId) return;
    const nextColours = selectedColours.includes(colour)
      ? selectedColours.filter((entry) => entry !== colour)
      : [...selectedColours, colour];
    const patch = buildSocketPatch(runtime, [
      { direction: 'output', socketId: 'colours', value: nextColours },
    ]);
    const resolvedPatch = {
      ...(Object.keys(patch).length > 0
        ? patch
        : { [VASE_FLOW_FIELD_IDS.chosenColours]: nextColours }),
      ...buildCustomiserCanonicalResetPatch({
        clearCombination: true,
      }),
    };
    runtime.patchFlowValues(runtime.flowId, resolvedPatch);
    emitConnectedBlockTrigger(runtime, 'coloursChanged');
  };

  const clearColours = () => {
    if (!runtime.flowId) return;
    const patch = buildSocketPatch(runtime, [
      { direction: 'output', socketId: 'colours', value: [] },
    ]);
    runtime.patchFlowValues(runtime.flowId, {
      ...(Object.keys(patch).length > 0
        ? patch
        : { [VASE_FLOW_FIELD_IDS.chosenColours]: [] }),
      ...buildCustomiserCanonicalResetPatch({
        clearCombination: true,
      }),
    });
  };

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-[24px] border border-stone-200 bg-white shadow-sm"
      {...interactionProps}
    >
      <div className="border-b border-stone-200/80 px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
          Colour Filter
        </div>
        <h3 className="text-sm font-semibold text-stone-900">Refine by colour</h3>
      </div>
      <div className="space-y-3 p-3">
        <div className="grid grid-cols-2 gap-2">
          {colourOptions.map((colour) => {
            const active = selectedColours.includes(colour);
            const swatch = swatchMap[colour];
            return (
              <button
                key={colour}
                type="button"
                disabled={!allowInteraction}
                onClick={() => toggleColour(colour)}
                className={`flex items-center gap-2 rounded-2xl border px-3 py-2 text-left transition ${
                  active
                    ? 'border-stone-900 bg-stone-900 text-white'
                    : 'border-stone-200 bg-stone-50 text-stone-700'
                } ${allowInteraction ? 'hover:border-stone-400 hover:bg-stone-100' : 'cursor-default opacity-80'}`}
              >
                <span
                  className="h-5 w-5 rounded-full border border-black/10 shadow-inner"
                  style={{
                    background:
                      swatch ||
                      'linear-gradient(135deg, rgba(231,229,228,1) 0%, rgba(214,211,209,1) 100%)',
                  }}
                />
                <span className="min-w-0 truncate text-xs font-medium">{colour}</span>
              </button>
            );
          })}
        </div>
        {!selectedFinish ? (
          <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-500">
            Select a finish first to load matching colours.
          </div>
        ) : null}
        {loading ? <div className="text-xs text-stone-500">Loading colours...</div> : null}
        {error ? <div className="text-xs text-red-600">{error}</div> : null}
        {selectedColours.length > 0 ? (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {selectedColours.map((colour) => (
                <span
                  key={`selected-${colour}`}
                  className="inline-flex items-center rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-medium text-stone-700"
                >
                  {colour}
                </span>
              ))}
            </div>
            <button
              type="button"
              disabled={!allowInteraction}
              onClick={clearColours}
              className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                allowInteraction
                  ? 'border-stone-300 bg-white text-stone-700 hover:border-stone-500 hover:bg-stone-50'
                  : 'cursor-default border-stone-200 bg-stone-100 text-stone-400'
              }`}
            >
              Clear colours
            </button>
          </div>
        ) : (
          <div className="text-xs text-stone-500">
            {selectedFinish
              ? 'Tap colours to narrow the matching combinations.'
              : 'Choose a finish to activate the colour filters.'}
          </div>
        )}
      </div>
      {selectionOverlay}
    </div>
  );
};

const CombinationResultsSurface: React.FC<ConnectedBlockSurfaceProps> = ({
  block,
  sectionId,
  isAdmin = false,
  isInteractPreview = false,
  selectionOverlay,
  onDebugSnapshot,
}) => {
  const runtime = useConnectedBlockRuntime(block, { sectionId });
  const selectedProduct = resolveCustomiserSelectedProduct(runtime);
  const selectedColours = resolveCustomiserSelectedColours(runtime, 'input');
  const selectedCombination = asRecord(
    runtime.getSocketValue('output', 'combination') ??
      getCanonicalCustomiserValue(runtime, VASE_FLOW_FIELD_IDS.chosenCombination)
  );
  const selectedFinish = resolveCustomiserSelectedFinish(runtime);
  const { combinations, loading, error, filterCombinations, setSelectedFinish } = useColours();
  const filteredCombinations = selectedFinish ? filterCombinations(selectedColours) : [];
  const selectedCombinationId =
    typeof selectedCombination?.id === 'string' ? selectedCombination.id : null;
  const resolvedSelectedCombination = selectedCombinationId
    ? filteredCombinations.find((entry) => entry.id === selectedCombinationId) ?? null
    : null;
  const allowInteraction = Boolean(
    (!isAdmin || isInteractPreview) && runtime.flowId && selectedProduct && selectedFinish
  );
  const activeFlow = runtime.flowDefinition ?? runtime.summary?.flow ?? null;
  const addToRequestNodeId = findFlowNodeIdByType(activeFlow, 'add-to-list');
  const removeFromRequestNodeId = findFlowNodeIdByType(activeFlow, 'remove-from-list');
  const clearRequestListNodeId = findFlowNodeIdByType(activeFlow, 'clear-list');
  const showRequestActions = Boolean(selectedProduct && addToRequestNodeId);
  const interactionProps = getBuilderInteractionProps(allowInteraction);

  useEffect(() => {
    if (!selectedFinish) return;
    setSelectedFinish(selectedFinish);
  }, [selectedFinish, setSelectedFinish]);

  const visibleRequestItems = runtime.flowId
    ? asRequestItemList(
        runtime.getFlowValue(runtime.flowId, REQUEST_FLOW_FIELD_IDS.requestItems)
      )
    : [];

  const combinationResultsDebugDetails = useMemo(
    () => ({
      selectedProduct: toDebugValue(selectedProduct),
      selectedFinish,
      selectedColours,
      selectedCombination: toDebugValue(selectedCombination),
      filteredCombinationCount: filteredCombinations.length,
      totalCombinationCount: combinations.length,
      visibleRequestItemCount: visibleRequestItems.length,
      allowInteraction,
      loading,
      error: error ?? null,
    }),
    [
      allowInteraction,
      combinations.length,
      error,
      filteredCombinations.length,
      loading,
      selectedColours,
      selectedCombination,
      selectedFinish,
      selectedProduct,
      visibleRequestItems.length,
    ]
  );
  useConnectedBlockDebugSnapshot(block, runtime, combinationResultsDebugDetails, onDebugSnapshot);

  const removeRequestItem = (itemId: string) => {
    if (!runtime.flowId || !removeFromRequestNodeId) return;
    const item = visibleRequestItems.find((entry) => entry.id === itemId);
    if (!item) return;
    removeRequestItemFromFlow({
      flowId: runtime.flowId,
      item,
      removeNodeId: removeFromRequestNodeId,
      patchFlowValues: runtime.patchFlowValues,
      invokeFlowNode: runtime.invokeFlowNode,
    });
  };

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-[24px] border border-stone-200 bg-gradient-to-b from-white to-stone-50/80 shadow-sm"
      {...interactionProps}
    >
      <div className="border-b border-stone-200/80 px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
          Curated Matches
        </div>
        <h3 className="text-sm font-semibold text-stone-900">
          {filteredCombinations.length} match{filteredCombinations.length === 1 ? '' : 'es'}
        </h3>
      </div>
      <div className="flex h-[calc(100%-57px)] min-h-0 flex-col overflow-hidden p-3">
        {!selectedProduct ? (
          <div className="rounded-2xl border border-dashed border-stone-200 bg-white/80 px-3 py-3 text-xs text-stone-500">
            Pick a vase, finish, and colours to see matching combinations here.
          </div>
        ) : !selectedFinish ? (
          <div className="rounded-2xl border border-dashed border-stone-200 bg-white/80 px-3 py-3 text-xs text-stone-500">
            Pick a finish to see matching combinations here.
          </div>
        ) : (
          <div className="min-h-0 flex flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <ColourCombinations
                combinations={filteredCombinations}
                loading={loading}
                error={error}
                selectedProduct={selectedProduct}
                selectedComboId={selectedCombinationId}
                onSelectCombo={(combo) => {
                  if (!allowInteraction || !runtime.flowId) return;
                  const patch = buildSocketPatch(runtime, [
                    { direction: 'output', socketId: 'combination', value: combo },
                  ]);
                  const resolvedPatch =
                    Object.keys(patch).length > 0
                      ? patch
                      : { [VASE_FLOW_FIELD_IDS.chosenCombination]: combo };
                  runtime.patchFlowValues(runtime.flowId, resolvedPatch);
                  emitConnectedBlockTrigger(runtime, 'combinationSelected');
                }}
                variant="curated"
                showHeading={false}
                showAddToOrderButton={false}
                listMaxHeightClassName="max-h-none overflow-visible pr-0"
              />
            </div>
            <div className="shrink-0 border-t border-stone-200/80 bg-white/95 px-1 pt-3 backdrop-blur-sm">
              {showRequestActions ? (
                resolvedSelectedCombination ? (
                  <div className="space-y-2">
                    <button
                      type="button"
                      disabled={!allowInteraction}
                      onClick={() => {
                        if (!runtime.flowId || !addToRequestNodeId) return;
                        runtime.invokeFlowNode(runtime.flowId, addToRequestNodeId);
                      }}
                      className={`w-full rounded-xl px-4 py-3 text-sm font-semibold transition ${
                        !allowInteraction
                          ? 'cursor-not-allowed border border-stone-300 bg-stone-200 text-stone-500'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      Add {selectedProduct?.name ?? 'Selection'} in{' '}
                      {resolvedSelectedCombination.color_description || 'Custom Combination'} to Request
                    </button>
                    {allowInteraction ? null : (
                      <p className="text-center text-xs text-stone-500">
                        Visible to visitors. Switch to Interact to test the action.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-stone-200 bg-white/80 px-3 py-3 text-xs text-stone-500">
                    Select a combination to enable Add To Request.
                  </div>
                )
              ) : selectedProduct ? (
                <div className="rounded-2xl border border-dashed border-stone-200 bg-white/80 px-3 py-3 text-xs text-stone-500">
                  Add an `Add to Request` node to this flow to enable request actions here.
                </div>
              ) : null}
            </div>
          </div>
        )}
        <div className="shrink-0">
          <RequestItemsPanel
            items={visibleRequestItems}
            canRemove={Boolean(allowInteraction && removeFromRequestNodeId)}
            canClear={Boolean(allowInteraction && clearRequestListNodeId && visibleRequestItems.length > 0)}
            showRemoveButton={Boolean(removeFromRequestNodeId)}
            showClearButton={Boolean(clearRequestListNodeId && visibleRequestItems.length > 0)}
            onRemoveItem={removeRequestItem}
            onClear={() => {
              if (!runtime.flowId || !clearRequestListNodeId) return;
              runtime.invokeFlowNode(runtime.flowId, clearRequestListNodeId);
            }}
          />
        </div>
      </div>
      {selectionOverlay}
    </div>
  );
};

export const ConnectedBlockSurface: React.FC<ConnectedBlockSurfaceProps> = (props) => {
  switch (props.block.type) {
    case 'product-deconstructor':
      return <ProductDeconstructorSurface {...props} />;
    case 'customiser-viewer':
      return <ViewerSurface {...props} />;
    case 'customiser-finish-picker':
      return <FinishPickerSurface {...props} />;
    case 'customiser-colour-filter':
      return <ColourFilterSurface {...props} />;
    case 'customiser-combination-results':
      return <CombinationResultsSurface {...props} />;
    default:
      return null;
  }
};

export default ConnectedBlockSurface;
